// Shared formatting + severity helpers for the Org 360 sections. Kept in one
// place so every section renders numbers, money, and limit severity the same
// way the standalone dashboards do.

export const fmtN = (n) =>
  typeof n === "number" && Number.isFinite(n) ? n.toLocaleString() : n ?? "0";

export const fmtMoney = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

export const fmtMoneyCompact = (value) => {
  const n = Number(value || 0);
  if (Math.abs(n) >= 1000) {
    return `$${(n / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}k`;
  }
  return fmtMoney(n);
};

export const formatRelative = (ms) => {
  if (!ms) return "—";
  const diff = Date.now() - ms;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  try {
    return new Date(ms).toLocaleDateString();
  } catch {
    return "—";
  }
};

// Usage-limits severity, matching OrganizationLimits/page.jsx semantics.
const OCR_KEY = "ocrClassificationPerOrgPerPeriod";
const PC_KEY = "pcAutoRunPerOrgPerPeriod";
const OCR_USAGE_KEY = "ocr_classification";
const PC_USAGE_KEY = "checklist_run";

export function getEffectiveQuotaCount(usage) {
  if (!usage || typeof usage !== "object") return 0;
  const raw = Number.isFinite(usage.count) ? usage.count : 0;
  const expired = usage.periodEndsAt && Date.now() >= usage.periodEndsAt;
  return expired ? 0 : raw;
}

export function severityForRatio(ratio) {
  if (!Number.isFinite(ratio) || ratio <= 0) return "ok";
  if (ratio >= 0.9) return "critical";
  if (ratio >= 0.7) return "watch";
  return "ok";
}

export const SEVERITY_COLOR = {
  critical: "#e11d48",
  watch: "#d97706",
  ok: "#059669",
  idle: "#94a3b8",
};

/**
 * Reduce a usage-limits org row down to the two metric usages + worst-case
 * severity used by the header badge and the Limits section meters.
 */
export function describeLimits(org) {
  if (!org) return null;
  const ocrUsage = org.currentUsage?.[OCR_USAGE_KEY] || {};
  const pcUsage = org.currentUsage?.[PC_USAGE_KEY] || {};
  const ocrLimit = org.aiUsageLimits?.[OCR_KEY] ?? org.systemDefaults?.[OCR_KEY];
  const pcLimit = org.aiUsageLimits?.[PC_KEY] ?? org.systemDefaults?.[PC_KEY];
  const ocrCount = getEffectiveQuotaCount(ocrUsage);
  const pcCount = getEffectiveQuotaCount(pcUsage);
  const ocrRatio = ocrLimit ? ocrCount / ocrLimit : 0;
  const pcRatio = pcLimit ? pcCount / pcLimit : 0;
  const worstSev = severityForRatio(Math.max(ocrRatio, pcRatio));
  return {
    ocr: { count: ocrCount, limit: ocrLimit, ratio: ocrRatio },
    pc: { count: pcCount, limit: pcLimit, ratio: pcRatio },
    worstSev,
    hasOverride:
      org.aiUsageLimits?.[OCR_KEY] != null || org.aiUsageLimits?.[PC_KEY] != null,
    autoRun: !!org.autoRunProcessingChecklist,
    splitting:
      !!org.enableDocumentSplitting || !!org.systemDefaults?.isSplitFlowAllowlisted,
    isNewFi: !!org.systemDefaults?.isNewFi,
  };
}
